import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoMdCall } from 'react-icons/io';

/**
 * Persistent indicator showing active call status
 * Always visible in header when there's an active call
 */
export function ActiveCallIndicator() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCall, setActiveCall] = useState<{
    callerPhone: string;
    callerId: string;
    roomName: string;
  } | null>(null);

  const checkActiveCall = React.useCallback(() => {
    const activeCallRoomName = sessionStorage.getItem('activeCallRoomName');
    const activeCallCustomer = sessionStorage.getItem('activeCallCustomer');
    const activeCallTimestamp = sessionStorage.getItem('activeCallTimestamp');

    if (activeCallRoomName && activeCallCustomer && activeCallTimestamp) {
      try {
        const customer = JSON.parse(activeCallCustomer);
        const timestamp = parseInt(activeCallTimestamp, 10);
        const now = Date.now();
        if (now - timestamp < 5 * 60 * 1000) {
          setActiveCall({
            callerPhone: customer.phone || '',
            callerId: customer.name || customer.phone || '',
            roomName: activeCallRoomName,
          });
          return;
        }
        sessionStorage.removeItem('activeCallRoomName');
        sessionStorage.removeItem('activeCallCustomer');
        sessionStorage.removeItem('activeCallTimestamp');
      } catch (_) {
        sessionStorage.removeItem('activeCallRoomName');
        sessionStorage.removeItem('activeCallCustomer');
        sessionStorage.removeItem('activeCallTimestamp');
      }
    }
    setActiveCall(null);
  }, []);

  useEffect(() => {
    checkActiveCall();
    const interval = setInterval(checkActiveCall, 1000);
    const onDisconnected = () => {
      setActiveCall(null);
    };
    window.addEventListener('callDisconnected', onDisconnected);
    return () => {
      clearInterval(interval);
      window.removeEventListener('callDisconnected', onDisconnected);
    };
  }, [checkActiveCall]);

  const handleNavigateToCall = () => {
    if (!activeCall) return;

    const callerPhoneClean = activeCall.callerPhone.replace(/[^\d]/g, '');
    const targetPath = `/events/whatsapp-send-message/${callerPhoneClean}/whatsappcalls`;
    
    navigate(targetPath);
  };

  if (!activeCall) {
    return null;
  }

  // Don't show if already on the call page
  const currentPath = location.pathname;
  if (currentPath.includes('/whatsapp-send-message/') && currentPath.includes('/whatsappcalls')) {
    return null;
  }

  return (
    <button
      onClick={handleNavigateToCall}
      className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors shadow-sm"
      title={`Active call with ${activeCall.callerId} - Click to go to call`}
    >
      <IoMdCall className="w-4 h-4 animate-pulse" />
      <span>Live Call</span>
    </button>
  );
}
